import type { AdminRouter, AppRouter } from "@bt/api";
import { createTRPCClient, httpBatchLink, httpLink, splitLink } from "@trpc/client";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import { apiPath, garage } from "./garage";

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

/** Procedures that work without a garage, so they also work where the URL's garage doesn't exist. */
function withoutGarage(path: string): boolean {
  return (
    path === "garages.list" ||
    path === "garages.create" ||
    ["system.", "account.", "keys.", "invitations.", "installation.", "publicDemo."].some(
      (prefix) => path.startsWith(prefix),
    )
  );
}

/** Queries whose input can be long; they go as POST, since a GET would put it in the URL. */
const LONG_INPUT = new Set(["orders.partsList"]);

/** A client whose garage procedures go to one garage's API (the admin's garage page uses others). */
export function createAppClient(name: string | null) {
  return createTRPCClient<AppRouter>({
    links: [
      splitLink({
        condition: (op) => name !== null && LONG_INPUT.has(op.path),
        true: httpLink({ url: apiPath("/trpc", name), methodOverride: "POST" }),
        false: splitLink({
          condition: (op) => name !== null && !withoutGarage(op.path),
          true: httpBatchLink({ url: apiPath("/trpc", name) }),
          false: httpBatchLink({ url: "/api/trpc" }),
        }),
      }),
    ],
  });
}

export const trpcClient = createAppClient(garage);

/** The admin interface's API (/api/admin/trpc), for platform admins. */
export const { TRPCProvider: AdminTRPCProvider, useTRPC: useAdminTRPC } =
  createTRPCContext<AdminRouter>();
export const adminClient = createTRPCClient<AdminRouter>({
  links: [httpBatchLink({ url: "/api/admin/trpc" })],
});

export type RouterOutputs = inferRouterOutputs<AppRouter>;
export type RouterInputs = inferRouterInputs<AppRouter>;
export type AdminOutputs = inferRouterOutputs<AdminRouter>;
